import { apiUrl, commitSha, releaseEnv } from '@algoan/utils/env';
import { datadogRum, RumEvent } from '@datadog/browser-rum';

type LogErrorProps =
  | {
      context?: Record<string, unknown> | undefined;
      fingerprint?: string | undefined;
      message: string;
    }
  | {
      context?: Record<string, unknown>;
      fingerprint?: string;
      error: Error;
    };

type Event = {
  type: 'error';
  context?: {
    [k: string]: unknown;
  };
  error?: { csp?: { [k: string]: unknown } };
};

type InitProps = {
  version: string;
  applicationId: string;
  clientToken: string;
  service: string;
  beforeSend?: (event: Event) => boolean;
};

export interface Monitoring {
  logError: (props: LogErrorProps) => void;
  logAction: typeof datadogRum.addAction;
  init: (props: InitProps) => void;
  getUser: typeof datadogRum.getUser;
  setUser: typeof datadogRum.setUser;
  setUserProperty: typeof datadogRum.setUserProperty;
  setGlobalContextProperty: typeof datadogRum.setGlobalContextProperty;
  addFeatureFlag: typeof datadogRum.addFeatureFlagEvaluation;
}

export const monitoring: Monitoring = {
  /**
   * Log an error to the monitoring platform.
   *
   * @param props.message the error message (not needed if error is passed)
   * @param props.error the Javascript error (not needed if message is passed)
   * @param props.fingerprint string allowing to customize the grouping behavior of errors (optional)
   * @param props.context context of the error (optional)
   */
  logError: (props: LogErrorProps): void => {
    const datadogError = 'error' in props ? props.error : new Error(props.message);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    datadogError.dd_fingerprint = props.fingerprint;
    datadogRum.addError(datadogError, props.context);
  },
  /**
   * Log a custom action to the monitoring platform.
   *
   * @param name Name of the action
   * @param context Context of the action
   */
  logAction: datadogRum.addAction,
  /**
   *
   * Initialize the monitoring platform.
   *
   * @param props.version the version of the application
   * @param props.applicationId the application id
   * @param props.clientToken the client token
   * @param props.service the service name
   */
  init: (props: InitProps) =>
    datadogRum.init({
      allowedTracingUrls: [
        apiUrl,
        ...(typeof window !== 'undefined' ? [new RegExp(`^${window.location.origin}/api`)] : []),
      ],
      applicationId: props.applicationId,
      clientToken: props.clientToken,
      defaultPrivacyLevel: 'mask-user-input',
      env: `algoan-${releaseEnv}`,
      version: `${props.version}-${commitSha}`,
      service: props.service,
      sessionReplaySampleRate: 20,
      sessionSampleRate: 100,
      site: 'datadoghq.eu',
      trackingConsent: 'granted',
      trackLongTasks: true,
      trackResources: true,
      trackUserInteractions: true,
      enableExperimentalFeatures: ['feature_flags'],
      storeContextsAcrossPages: true,
      beforeSend: (event: RumEvent) => {
        if (props.beforeSend && event.type === 'error') {
          return props.beforeSend(event);
        }
        return true;
      },
    }),
  /**
   * Get the user id
   */
  getUser: datadogRum.getUser,
  /**
   * Set the user id
   */
  setUser: datadogRum.setUser,
  /**
   * Set the user properties, stored in `@usr.<key>`
   *
   * @param key Key of the property
   * @param property Value of the property
   */
  setUserProperty: datadogRum.setUserProperty,
  /**
   * Set the global context property, stored in `@context.<key>`
   *
   * @param key Key of the property
   * @param property Value of the property
   */
  setGlobalContextProperty: datadogRum.setGlobalContextProperty,
  /**
   * Add a feature flag
   *
   * @param key Key of the feature flag
   * @param value Value of the feature flag
   */
  addFeatureFlag: datadogRum.addFeatureFlagEvaluation,
};
